import { useState, useRef, useEffect, ReactNode } from 'react';

interface ExpandableProps {
  header: ReactNode;
  content?: ReactNode;
  defaultExpanded?: boolean;
}

export default function Expandable({
  header,
  content,
  defaultExpanded = false
}: ExpandableProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [contentHeight, setContentHeight] = useState<number | undefined>(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      if (isExpanded) {
        // Start animation with calculated height
        setContentHeight(contentRef.current.scrollHeight);
        setIsAnimating(true);
        // After animation completes, switch to auto height
        const timer = setTimeout(() => {
          setIsAnimating(false);
        }, 300);
        return () => clearTimeout(timer);
      } else {
        // For closing, first set the current height, then animate to 0
        setContentHeight(contentRef.current.scrollHeight);
        setIsAnimating(true);
        requestAnimationFrame(() => {
          setContentHeight(0);
        });
      }
    }
  }, [isExpanded]);

  return (
    <div
      style={{
        backgroundColor: 'var(--color-base-100)',
        border: '2px solid var(--color-base-300)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: 0,
          display: 'block',
          backgroundColor: 'var(--color-base-200)',
          border: 'none',
          cursor: content ? 'pointer' : 'default',
          textAlign: 'left',
          transition: 'background-color 0.2s ease',
        }}
      >
        {header}
      </button>

      {/* Collapsible Content */}
      <div
        style={{
          height: isExpanded && !isAnimating ? 'auto' : contentHeight,
          overflow: isExpanded && !isAnimating ? 'visible' : 'hidden',
          transition: 'height 0.3s ease',
        }}
      >
        <div ref={contentRef}>
          {content}
        </div>
      </div>
    </div>
  );
}
